/** biome-ignore-all lint/style/useNamingConvention: External APIs (Google One Tap, OneTrust, tracking) use different naming conventions */
'use client';

import useBreakpoint from 'hooks/useBreakpoint';
import type { PageType } from 'interfaces/content/articles/Post';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { trackOptimizelyEvent } from 'providers/OptimizelyProvider';
import { OPTIMIZELY_EVENT_VALUES } from 'providers/OptimizelyProvider/utils';
import { useCallback, useEffect, useState } from 'react';
import type { GoogleAuthResult, GoogleOneTapCredentialResponse } from 'services/GoogleOneTap/types';
import { trackEvent } from 'services/Gtm/functions';
import { CookieService } from '@/app_lib/CookieService';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';

// TODO: look into whether we should be showing our domains on the client.
import { userTokenCookieDomain } from '@/app_lib/utils/clientCookieUtils';
import { geoIp } from '@/app_lib/utils/paymentApi/geoip';
import { createGoogleUserSession, verifyGoogleIdToken } from './verifyToken';

// Constants
const ONETRUST_API_READY_DELAY_MS = 50; // Wait time for OneTrust API to be ready
const PAGE_RELOAD_DELAY_MS = 100; // Delay before reloading page after successful auth
const GOOGLE_SCRIPT_CHECK_INTERVAL_MS = 50; // Interval to check if Google script is loaded
const INTERSECTION_OBSERVER_THRESHOLD = 0.1; // Trigger when 10% of element is visible

interface GoogleOneTapConfig {
  auto_select: boolean;
  cancel_on_tap_outside: boolean;
  client_id: string;
  context: string;
  use_fedcm_for_prompt: boolean;
  callback: (response: GoogleOneTapCredentialResponse) => void | Promise<void>;
  moment_callback: (notification: unknown) => void;
}

interface OneTrustApi {
  IsAlertBoxClosed?: () => boolean;
}

interface GoogleMomentNotification {
  isDisplayed?: () => boolean;
  isDismissedMoment?: () => boolean;
  isSkippedMoment?: () => boolean;
  getDismissedReason?: () => string;
  getSkippedReason?: () => string;
}

interface SessionResult {
  success: boolean;
  pianoUid?: string;
  pianoToken?: string;
  isNewUser?: boolean;
  message?: string;
  error?: string;
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleOneTapConfig) => void;
          prompt: (momentCallback?: (notification: unknown) => void) => void;
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
    OnetrustActiveGroups?: string;
  }

  interface WindowEventMap {
    OneTrustGroupsUpdated: CustomEvent<string[]>;
    OTConsentApplied: CustomEvent;
    OneTrustPCLoaded: CustomEvent;
  }
}

interface GoogleOneTapProps {
  clientId: string;
  onSuccess?: (userData: GoogleAuthResult) => void;
  onError?: (error: string) => void;
  pageType: PageType;
}

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ clientId, onSuccess, onError, pageType }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [oneTrustConsentReady, setOneTrustConsentReady] = useState<boolean | null>(null);
  const isMobile = useBreakpoint('mdMax');
  const isTablet = useBreakpoint('lgMax');
  const pathname = usePathname();

  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  // Don't show Google One Tap on checkout pages
  const isCheckoutPage = pathname?.includes('/checkout/') ?? false;

  let deviceType: DeviceType;
  if (isMobile) {
    deviceType = 'mobile';
  } else if (isTablet) {
    deviceType = 'tablet';
  } else {
    deviceType = 'desktop';
  }

  useEffect(() => {
    // Check if user is already logged in by checking Piano session cookies
    const utpCookie = CookieService.get('__utp');
    setIsLoggedIn(Boolean(utpCookie));

    // Check if OneTrust consent was closed using official API
    const checkOneTrustClosed = (callback: (result: boolean) => void) => {
      setTimeout(() => {
        // biome-ignore lint/suspicious/noExplicitAny: Need to access OneTrust API
        const oneTrustApi = (window as any).OneTrust;

        // If OneTrust API is not available or IsAlertBoxClosed is not a function,
        // it means OneTrust banner is not shown at all, so we can show Google One Tap
        if (!oneTrustApi?.IsAlertBoxClosed || typeof oneTrustApi.IsAlertBoxClosed !== 'function') {
          callback(true);
          return;
        }

        // Use official OneTrust API to check if banner was closed/dismissed
        const isAlertBoxClosed = oneTrustApi.IsAlertBoxClosed();
        callback(isAlertBoxClosed);
      }, ONETRUST_API_READY_DELAY_MS);
    };

    // Handle OneTrustGroupsUpdated event - this fires when OneTrust is loaded/updated
    const handleOneTrustGroupsUpdated = (_event: CustomEvent) => {
      // Check if OneTrust consent banner was closed (with built-in delay)
      checkOneTrustClosed((isOneTrustClosed) => {
        setOneTrustConsentReady(isOneTrustClosed);
      });
    };

    // Add event listener for OneTrustGroupsUpdated
    window.addEventListener('OneTrustGroupsUpdated', handleOneTrustGroupsUpdated as EventListener);

    // Initial check - if OneTrust API is available, check immediately
    checkOneTrustClosed((isOneTrustClosed) => {
      setOneTrustConsentReady(isOneTrustClosed);
    });

    // Cleanup
    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', handleOneTrustGroupsUpdated as EventListener);
    };
  }, []);

  useEffect(() => {
    let observedElement: Element | null = null;
    let resizeObserver: ResizeObserver | null = null;

    // Function to check paywall height and cancel Google One Tap if visible
    const checkAndCancelPaywall = (element: Element) => {
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && window.google?.accounts?.id?.cancel) {
        window.google.accounts.id.cancel();
      }
    };

    // ResizeObserver to watch for height changes on the paywall element
    resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0 && entries[0]) {
        checkAndCancelPaywall(entries[0].target);
      }
    });

    // Handle when paywall element is found
    const handlePaywallFound = (paywallElement: Element) => {
      // Disconnect previous observation if element changed
      if (observedElement && resizeObserver) {
        resizeObserver.unobserve(observedElement);
      }

      // Check height immediately
      checkAndCancelPaywall(paywallElement);

      // Start observing for height changes
      if (resizeObserver) {
        resizeObserver.observe(paywallElement);
      }
      observedElement = paywallElement;
    };

    // Handle when paywall element is removed
    const handlePaywallRemoved = () => {
      if (observedElement && resizeObserver) {
        resizeObserver.unobserve(observedElement);
      }
      observedElement = null;
    };

    // Function to find and observe the paywall element
    const findAndObservePaywall = () => {
      const paywallElement = document.querySelector('.paywallax-ii-selector');

      if (paywallElement && paywallElement !== observedElement) {
        handlePaywallFound(paywallElement);
      } else if (!paywallElement && observedElement) {
        handlePaywallRemoved();
      }
    };

    // Initial check
    findAndObservePaywall();

    // MutationObserver to detect when paywall element is added/removed
    const mutationObserver = new MutationObserver(() => {
      findAndObservePaywall();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      mutationObserver.disconnect();
    };
  }, []);

  const handleAuthError = useCallback(
    (error: string, errorType: string) => {
      trackEvent({
        customData: {
          deviceType,
          error: errorType,
          pageType,
          user_registration_type: 'google_one_tap',
        },
        eventAction: 'error/drop-off',
        eventLabel: 'google_one_tap_login_error',
      });
      onError?.(error);
    },
    [pageType, deviceType, onError],
  );

  const handleSuccessfulAuth = useCallback(
    async (sessionResult: SessionResult) => {
      // Set Piano session cookies
      if (sessionResult.pianoToken && sessionResult.pianoUid) {
        const { domain } = userTokenCookieDomain();
        CookieService.set('__utp', sessionResult.pianoToken, {
          sameSite: 'strict',
          secure: true,
          ...(domain && { domain }),
        });
        CookieService.set('pianoUid', sessionResult.pianoUid);
      }

      // Immediately update login state to prevent duplicate prompts
      setIsLoggedIn(true);

      // Create success data
      const successData: GoogleAuthResult = {
        accessToken: sessionResult.pianoToken,
        isNewUser: sessionResult.isNewUser,
        message: sessionResult.message,
        pianoUid: sessionResult.pianoUid,
        success: true,
      };

      // Track success events
      trackEvent({
        customData: {
          deviceType,
          isNewUser: JSON.stringify(sessionResult.isNewUser),
          pageType,
          user_registration_type: 'google_one_tap',
        },
        eventAction: 'conversion',
        eventLabel: 'google_one_tap_login_success',
      });
      trackOptimizelyEvent('user_signed_in', {});

      if (sessionResult.isNewUser) {
        trackEvent({
          customData: {
            deviceType,
            isNewUser: JSON.stringify(sessionResult.isNewUser),
            pageType,
            user_registration_type: 'google_one_tap',
          },
          eventAction: 'conversion',
          eventLabel: 'google_one_tap_user_created',
        });
        trackOptimizelyEvent('user_signed_up', { value: OPTIMIZELY_EVENT_VALUES.signUpValue });

        if (sessionResult.pianoUid && sessionResult.pianoToken) {
          await geoIp(sessionResult.pianoUid, 'google_one_tap', sessionResult.pianoToken);
        }
      }

      onSuccess?.(successData);

      // Reload page after slight delay
      setTimeout(() => {
        window.location.reload();
      }, PAGE_RELOAD_DELAY_MS);
    },
    [pageType, deviceType, onSuccess],
  );

  const processAuthentication = useCallback(
    async (credential: string) => {
      // Verify Google ID token
      const verificationResult = await verifyGoogleIdToken(credential);

      if (!verificationResult.success) {
        handleAuthError(verificationResult.error || 'Token verification failed', 'token_verification_failed');
        return;
      }

      trackEvent({
        customData: { deviceType, pageType, user_registration_type: 'google_one_tap' },
        eventAction: 'click/engagement',
        eventLabel: 'google_one_tap_credential_received',
      });
      trackOptimizelyEvent('google_one_tap_continue', {});

      if (!verificationResult.payload) {
        handleAuthError('Invalid token payload', 'invalid_token_payload');
        return;
      }

      // Create user session
      const sessionResult = await createGoogleUserSession(verificationResult.payload);

      if (!sessionResult.success) {
        handleAuthError(sessionResult.error || 'Session creation failed', 'session_creation_failed');
        return;
      }

      handleSuccessfulAuth(sessionResult);
    },
    [handleAuthError, handleSuccessfulAuth, pageType, deviceType],
  );

  const handleCredentialResponse = useCallback(
    async (response: GoogleOneTapCredentialResponse) => {
      if (isProcessing || isLoggedIn) {
        return;
      }

      setIsProcessing(true);

      try {
        await processAuthentication(response.credential);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to process Google One Tap response';
        onError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, isLoggedIn, processAuthentication, onError],
  );

  const momentCallback = useCallback(
    (notification: GoogleMomentNotification) => {
      if (notification.isDisplayed?.()) {
        trackOptimizelyEvent('google_one_tap_shown', {});
        trackEvent({
          customData: {
            deviceType,
            pageType,
            user_registration_type: 'google_one_tap',
          },
          eventAction: 'impression',
          eventLabel: 'google_one_tap_prompt_shown',
        });
      }

      // Use isDismissedMoment() to detect when user dismisses the prompt
      if (notification.isDismissedMoment?.()) {
        trackOptimizelyEvent('google_one_tap_dismissed', { value: notification.getDismissedReason?.() || 'unknown' });
        trackEvent({
          customData: {
            deviceType,
            pageType,
            user_registration_type: 'google_one_tap',
          },
          eventAction: `click/engagement${notification.getDismissedReason ? ` - ${notification.getDismissedReason()}` : ''}`,
          eventLabel: 'google_one_tap_prompt_dismissed',
        });
      }

      // Use isSkippedMoment() to detect when user skips (taps outside)
      if (notification.isSkippedMoment?.()) {
        trackOptimizelyEvent('google_one_tap_skipped', { value: notification.getSkippedReason?.() || 'unknown' });
        trackEvent({
          customData: {
            deviceType,
            pageType,
            user_registration_type: 'google_one_tap',
          },
          eventAction: `click outside/engagement${notification.getSkippedReason ? ` - ${notification.getSkippedReason()}` : ''}`,
          eventLabel: 'google_one_tap_prompt_skipped',
        });
      }
    },
    [deviceType, pageType],
  );

  // Don't initialize if paywall is active, pending, user is logged in, processing, OneTrust consent not ready, or on checkout pages
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    let intersectionObserver: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    // Helper function to cancel Google One Tap if the API is loaded
    const cancelGoogleOneTap = () => {
      if (window.google?.accounts?.id?.cancel) {
        window.google.accounts.id.cancel();
      }
    };

    // Get an elements position relative to the viewport - only the top
    const checkIfVideoPassedViewport = (element: Element) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom < 0) {
        cancelGoogleOneTap();
        return true;
      }
      return false;
    };

    // Checks if video was scrolled past, if not, sets up IntersectionObserver to detect when video enters viewport
    const setupRelatedVideoObserver = (element: Element) => {
      intersectionObserver?.disconnect();

      if (checkIfVideoPassedViewport(element)) {
        return;
      }

      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              cancelGoogleOneTap();
            }
          });
        },
        { threshold: INTERSECTION_OBSERVER_THRESHOLD },
      );
      intersectionObserver.observe(element);
    };

    // Check for #related-video to set up IntersectionObserver
    const relatedVideoElement = document.getElementById('related-video');
    if (relatedVideoElement) {
      setupRelatedVideoObserver(relatedVideoElement);
    }

    const targetNode = document.querySelector('.article-content');

    // Check for DOM lazy load of #related-video
    mutationObserver = new MutationObserver(() => {
      if (!intersectionObserver) {
        const element = document.getElementById('related-video');
        if (element) {
          setupRelatedVideoObserver(element);
        }
      }
    });

    if (targetNode) {
      mutationObserver.observe(targetNode, {
        childList: true,
        subtree: true,
      });
    }

    // Backup for before IntersectionObserver is triggered
    const handleScroll = () => {
      const element = document.getElementById('related-video');
      if (element) {
        checkIfVideoPassedViewport(element);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      intersectionObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  useEffect(() => {
    if (paywallCheck === 'paywalled' || isLoggedIn || isProcessing || !oneTrustConsentReady || isCheckoutPage) {
      return;
    }
    const initializeGoogleOneTap = () => {
      if (window.google?.accounts?.id) {
        const config: GoogleOneTapConfig = {
          auto_select: false,
          callback: handleCredentialResponse,
          cancel_on_tap_outside: true,
          client_id: clientId,
          context: 'signin',
          moment_callback: momentCallback,
          use_fedcm_for_prompt: true,
        };

        window.google.accounts.id.initialize(config);
        window.google.accounts.id.prompt(momentCallback);
      }
    };

    if (window.google?.accounts?.id) {
      initializeGoogleOneTap();
    } else {
      // Wait for the script to load
      const checkGoogleLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleOneTap();
        }
      }, GOOGLE_SCRIPT_CHECK_INTERVAL_MS);

      return () => {
        clearInterval(checkGoogleLoaded);
      };
    }
  }, [
    clientId,
    handleCredentialResponse,
    paywallCheck,
    isLoggedIn,
    isProcessing,
    oneTrustConsentReady,
    momentCallback,
    isCheckoutPage,
  ]);

  if (isLoggedIn === null || oneTrustConsentReady === null) {
    return null;
  }

  if (isLoggedIn || isProcessing || !oneTrustConsentReady || isCheckoutPage) {
    return null;
  }

  return (
    <Script
      src='https://accounts.google.com/gsi/client'
      strategy='afterInteractive'
    />
  );
};

// biome-ignore lint/style/noDefaultExport: Default export is used for component consistency
export default GoogleOneTap;
